import { useMemo } from 'react';
import useAppStore from '../store/index';
import { calculateAllMetrics, useCalculationsSelector } from '../store/selectors/calculations';
import { useShallow } from 'zustand/react/shallow';
import { forceNumber, safeDiv } from '../store/utils';
import {
    PEProfitCalculations,
    PEProfitPrognose,
    PEPnlBlock,
    PEPnlAbweichung,
    PEProfitAnsichtsModus,
    PlanungsBasisDatenTyp,
    SollIstVergleichRow,
    PersonalAnpassung,
    GemeinkostenDetailAnpassung,
    calcAutoKundenVerlustProzent,
    createDefaultPEProfitPrognose,
    PE_PROFIT_NEUER_MITARBEITER_ID,
    PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU,
    PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER,
    PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
} from '../types/peProfit';
import { selectActivePeProfitWorkspace } from '../store/peProfitWorkspace';

const roundEuro = (value: number): number => Math.round(Number(forceNumber(value)));

const num = (value: unknown): number => Number(forceNumber(value));

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// PURE CALCULATION (ohne React — testbar / wiederverwendbar)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

type CalcSnapshot = ReturnType<typeof useCalculationsSelector>;

type CalcSnapshotWithPlanungsBasis = CalcSnapshot & {
    peProfitBasisUmsatzMonat?: number;
    peProfitBasisUmsatzJahr?: number;
    peProfitBasisRentabilitaetProzent?: number;
    peProfitBasisInhaberertragMonat?: number;
};

const withPlanungsBasisOverrides = (
    calc: CalcSnapshot,
    overrides: {
        umsatzMonat: number;
        umsatzJahr: number;
        rentabilitaetProzent: number;
        inhaberertragMonat: number;
    }
): CalcSnapshotWithPlanungsBasis => {
    const bwaData = (calc.bwaData ?? {}) as Record<string, unknown>;

    return {
        ...calc,
        bwaData: {
            ...bwaData,
            umsatz: overrides.umsatzJahr,
        },
        monatsumsatzNetto: overrides.umsatzMonat,
        gesamtUmsatz: overrides.umsatzJahr,
        peProfitBasisUmsatzMonat: overrides.umsatzMonat,
        peProfitBasisUmsatzJahr: overrides.umsatzJahr,
        peProfitBasisRentabilitaetProzent: overrides.rentabilitaetProzent,
        peProfitBasisInhaberertragMonat: overrides.inhaberertragMonat,
    };
};

const isNeutralPeProfitPrognose = (prognose: PEProfitPrognose): boolean => {
    if (prognose.preisAnpassungProzent !== 0) return false;
    if (prognose.kundenVerlustProzent !== null && prognose.kundenVerlustProzent !== 0) return false;
    if (prognose.materialKostenAnpassungProzent !== 0) return false;
    if (prognose.materialZielMonat !== null) return false;
    if ((prognose.unternehmerUmsatzDeltaMonat ?? 0) !== 0) return false;
    if ((prognose.gemeinkostenDetailAnpassungen ?? []).length > 0) return false;
    if ((prognose.personalAnpassungen ?? []).some((item) => item.aktiv)) return false;
    if ((prognose.neueDienstleistungen ?? []).some((item) => item.aktiv)) return false;
    if ((prognose.neueInvestitionen ?? []).some((item) => item.aktiv)) return false;

    return true;
};

/**
 * Netto-Jahreswirkung des Unternehmer-Umsatz-Deltas auf den Gesamtumsatz.
 * Delta ≠ 0 wird immer angerechnet. Bei Minus-Delta: Auffang % mindert den Abzug (Team fängt auf).
 */
const calcUnternehmerUmsatzNettoJahr = (
    deltaMonat: number,
    auffangProzent: number,
    zeitraum: number
): { deltaJahr: number; nettoAufGesamtJahr: number } => {
    const deltaJahr = roundEuro(num(deltaMonat) * num(zeitraum));
    if (deltaJahr === 0) {
        return { deltaJahr, nettoAufGesamtJahr: 0 };
    }
    if (deltaJahr > 0) {
        return { deltaJahr, nettoAufGesamtJahr: deltaJahr };
    }
    const verloren = Math.abs(deltaJahr);
    const auffang = Math.min(100, Math.max(0, num(auffangProzent))) / 100;
    const nettoVerlust = roundEuro(verloren * (1 - auffang));
    return { deltaJahr, nettoAufGesamtJahr: -nettoVerlust };
};

const clonePnlBlock = (block: PEPnlBlock): PEPnlBlock => ({ ...block });

const stripActivePrognoseZeilen = (clientData: Record<string, unknown>): Record<string, unknown> => {
    const bwaData = (clientData.bwaData ?? {}) as Record<string, unknown>;
    const prognoseZeilen = Array.isArray(bwaData.prognoseZeilen) ? bwaData.prognoseZeilen : [];

    return {
        ...clientData,
        bwaData: {
            ...bwaData,
            prognoseZeilen: prognoseZeilen.map((zeile: Record<string, unknown>) => ({
                ...zeile,
                aktiv: false,
            })),
        },
    };
};

export const resolveCalcForPlanungsBasis = (
    calc: CalcSnapshot,
    basisDatenTyp: PlanungsBasisDatenTyp,
    clientData: unknown,
    settings: unknown
): CalcSnapshotWithPlanungsBasis => {
    const zeitraum = num(calc?.zeitraum) || 12;

    if (basisDatenTyp === 'start') {
        const safeClientData = (clientData ?? {}) as Record<string, unknown>;
        const startCalc = calculateAllMetrics(stripActivePrognoseZeilen(safeClientData), settings);
        const umsatzMonat = num(startCalc.monatsumsatzNetto);

        return withPlanungsBasisOverrides(startCalc, {
            umsatzMonat,
            umsatzJahr: umsatzMonat * zeitraum,
            rentabilitaetProzent: num(startCalc.rentabilitaetSoll),
            inhaberertragMonat: num(startCalc.inhaberErtragSoll),
        });
    }

    const umsatzMonat = num(calc.durchschnittsUmsatzIstNetto);

    return withPlanungsBasisOverrides(calc, {
        umsatzMonat,
        umsatzJahr: umsatzMonat * zeitraum,
        rentabilitaetProzent: num(calc.rentabilitaetIst) * 100,
        inhaberertragMonat: num(calc.inhaberErtragIst),
    });
};

const emptyPnlBlock = (): PEPnlBlock => ({
    umsatzNetto: 0,
    wareneinsatz: 0,
    rohertrag: 0,
    personalkostenProduktiv: 0,
    personalkostenUnproduktiv: 0,
    betriebskosten: 0,
    geraetekosten: 0,
    sonstigeEinnahmen: 0,
    investitionen: 0,
    gesamtkosten: 0,
    betriebsergebnis: 0,
    einkommensteuer: 0,
    inhaberertrag: 0,
    rentabilitaetProzent: 0,
    materialquoteProzent: 0,
});

const applyBwaBetriebsergebnis = (
    block: PEPnlBlock,
    betriebsergebnis: number,
    unternehmerBedarfMonat: number,
    einkommensteuer: number
): PEPnlBlock => {
    const inhaberertrag = roundEuro(betriebsergebnis + num(unternehmerBedarfMonat) + num(einkommensteuer));
    const rentabilitaetProzent =
        num(block.umsatzNetto) > 0 ? (inhaberertrag / num(block.umsatzNetto)) * 100 : 0;

    return {
        ...block,
        betriebsergebnis: roundEuro(betriebsergebnis),
        inhaberertrag,
        rentabilitaetProzent,
    };
};

const calcDeltaBetriebsergebnis = (params: {
    ist: PEPnlBlock;
    prognoseUmsatz: number;
    prognoseWareneinsatz: number;
    prognosePersonalkostenProduktiv: number;
    prognosePersonalkostenUnproduktiv: number;
    prognoseBetriebskosten: number;
    prognoseGeraetekosten: number;
    prognoseSonstigeEinnahmen: number;
    prognoseInvestitionen: number;
}): number => {
    const {
        ist,
        prognoseUmsatz,
        prognoseWareneinsatz,
        prognosePersonalkostenProduktiv,
        prognosePersonalkostenUnproduktiv,
        prognoseBetriebskosten,
        prognoseGeraetekosten,
        prognoseSonstigeEinnahmen,
        prognoseInvestitionen,
    } = params;

    const istPersonalGesamt = num(ist.personalkostenProduktiv) + num(ist.personalkostenUnproduktiv);
    const prognosePersonalGesamt = num(prognosePersonalkostenProduktiv) + num(prognosePersonalkostenUnproduktiv);

    return roundEuro(
        num(ist.betriebsergebnis) +
            (num(prognoseUmsatz) - num(ist.umsatzNetto)) -
            (num(prognoseWareneinsatz) - num(ist.wareneinsatz)) -
            (prognosePersonalGesamt - istPersonalGesamt) -
            (num(prognoseBetriebskosten) - num(ist.betriebskosten)) -
            (num(prognoseGeraetekosten) - num(ist.geraetekosten)) -
            (num(prognoseInvestitionen) - num(ist.investitionen)) +
            (num(prognoseSonstigeEinnahmen) - num(ist.sonstigeEinnahmen))
    );
};

const buildPnlBlock = (params: {
    umsatzNetto: number;
    wareneinsatz: number;
    personalkostenProduktiv: number;
    personalkostenUnproduktiv: number;
    betriebskosten: number;
    geraetekosten: number;
    sonstigeEinnahmen: number;
    investitionen: number;
    einkommensteuer: number;
    unternehmerBedarfMonat: number;
    istUmsatzJahr: number;
    materialJahr: number;
    betriebsergebnisOverride?: number;
}): PEPnlBlock => {
    const {
        umsatzNetto,
        wareneinsatz,
        personalkostenProduktiv,
        personalkostenUnproduktiv,
        betriebskosten,
        geraetekosten,
        sonstigeEinnahmen,
        investitionen,
        einkommensteuer,
        unternehmerBedarfMonat,
        istUmsatzJahr,
        materialJahr,
        betriebsergebnisOverride,
    } = params;

    const rohertrag = num(umsatzNetto) - num(wareneinsatz);
    const gesamtkosten =
        num(wareneinsatz) +
        num(personalkostenProduktiv) +
        num(personalkostenUnproduktiv) +
        num(betriebskosten) +
        num(geraetekosten) +
        num(investitionen) -
        num(sonstigeEinnahmen);
    const betriebsergebnis =
        betriebsergebnisOverride !== undefined
            ? num(betriebsergebnisOverride)
            : num(umsatzNetto) - gesamtkosten;
    const inhaberertrag = betriebsergebnis + num(unternehmerBedarfMonat) + num(einkommensteuer);
    const rentabilitaetProzent = num(umsatzNetto) > 0 ? (inhaberertrag / num(umsatzNetto)) * 100 : 0;
    const materialquoteProzent = num(istUmsatzJahr) > 0 ? (num(materialJahr) / num(istUmsatzJahr)) * 100 : 0;

    return {
        umsatzNetto: roundEuro(umsatzNetto),
        wareneinsatz: roundEuro(wareneinsatz),
        rohertrag: roundEuro(rohertrag),
        personalkostenProduktiv: roundEuro(personalkostenProduktiv),
        personalkostenUnproduktiv: roundEuro(personalkostenUnproduktiv),
        betriebskosten: roundEuro(betriebskosten),
        geraetekosten: roundEuro(geraetekosten),
        sonstigeEinnahmen: roundEuro(sonstigeEinnahmen),
        investitionen: roundEuro(investitionen),
        gesamtkosten: roundEuro(gesamtkosten),
        betriebsergebnis: roundEuro(betriebsergebnis),
        einkommensteuer: roundEuro(einkommensteuer),
        inhaberertrag: roundEuro(inhaberertrag),
        rentabilitaetProzent,
        materialquoteProzent,
    };
};

const buildLinearPrognosePnl = (params: {
    umsatzNetto: number;
    wareneinsatz: number;
    personalkostenProduktiv: number;
    personalkostenUnproduktiv: number;
    betriebskosten: number;
    geraetekosten: number;
    sonstigeEinnahmen: number;
    investitionen: number;
    einkommensteuer: number;
    unternehmerBedarfMonat: number;
    betriebsergebnis: number;
}): PEPnlBlock => {
    const {
        umsatzNetto,
        wareneinsatz,
        personalkostenProduktiv,
        personalkostenUnproduktiv,
        betriebskosten,
        geraetekosten,
        sonstigeEinnahmen,
        investitionen,
        einkommensteuer,
        unternehmerBedarfMonat,
        betriebsergebnis,
    } = params;

    const rohertrag = num(umsatzNetto) - num(wareneinsatz);
    const gesamtkosten =
        num(wareneinsatz) +
        num(personalkostenProduktiv) +
        num(personalkostenUnproduktiv) +
        num(betriebskosten) +
        num(geraetekosten) +
        num(investitionen) -
        num(sonstigeEinnahmen);
    const materialquoteProzent = num(umsatzNetto) > 0 ? (num(wareneinsatz) / num(umsatzNetto)) * 100 : 0;

    return applyBwaBetriebsergebnis(
        {
            umsatzNetto: roundEuro(umsatzNetto),
            wareneinsatz: roundEuro(wareneinsatz),
            rohertrag: roundEuro(rohertrag),
            personalkostenProduktiv: roundEuro(personalkostenProduktiv),
            personalkostenUnproduktiv: roundEuro(personalkostenUnproduktiv),
            betriebskosten: roundEuro(betriebskosten),
            geraetekosten: roundEuro(geraetekosten),
            sonstigeEinnahmen: roundEuro(sonstigeEinnahmen),
            investitionen: roundEuro(investitionen),
            gesamtkosten: roundEuro(gesamtkosten),
            betriebsergebnis: 0,
            einkommensteuer: roundEuro(einkommensteuer),
            inhaberertrag: 0,
            rentabilitaetProzent: 0,
            materialquoteProzent,
        },
        betriebsergebnis,
        unternehmerBedarfMonat,
        einkommensteuer
    );
};

/** GK-Prognose: BASIS - SUM(istWert) + SUM(neuerZielwert) — alles Monatswerte. */
const calcGkPrognoseMonat = (
    istGemeinkostenMonat: number,
    anpassungen: GemeinkostenDetailAnpassung[]
): number => {
    const sumIst = anpassungen.reduce((sum, a) => sum + Math.round(Number(a.istWert)), 0);
    const sumZiel = anpassungen.reduce((sum, a) => sum + Math.round(Number(a.neuerZielwert)), 0);
    return roundEuro(num(istGemeinkostenMonat) - sumIst + sumZiel);
};

/** Personal-Prognose: BASIS - SUM(istWert) + SUM(neuerZielwert) — nur aktive Anpassungen. */
const calcPersonalPrognoseMonat = (
    istPersonalkostenMonat: number,
    anpassungen: PersonalAnpassung[]
): number => {
    const aktiv = anpassungen.filter((a) => a.aktiv);
    const sumIst = aktiv.reduce((sum, a) => sum + Math.round(Number(a.istWert)), 0);
    const sumZiel = aktiv.reduce((sum, a) => {
        const ziel = a.entlassen ? 0 : Math.round(Number(a.neuerZielwert));
        return sum + ziel;
    }, 0);
    return roundEuro(num(istPersonalkostenMonat) - sumIst + sumZiel);
};

/** Umsatz- und Material-Effekte durch neue Mitarbeiter bzw. Entlassungen. */
const calcPersonalUmsatzDynamik = (
    anpassungen: PersonalAnpassung[],
    zeitraum: number,
    istMaterialQuote: number
): {
    umsatzNeuMitarbeiterJahr: number;
    umsatzEntlassungVerlustJahr: number;
    materialZusatzNeuMitarbeiterMonat: number;
} => {
    let umsatzNeuMitarbeiterJahr = 0;
    let umsatzEntlassungVerlustJahr = 0;
    let materialZusatzNeuMitarbeiterMonat = 0;

    for (const pa of anpassungen.filter((a) => a.aktiv)) {
        if (pa.mitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID && num(pa.neuerZielwert) > 0) {
            const faktor = num(pa.umsatzFaktorNeu ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU);
            const zusatzMonat = num(pa.neuerZielwert) * faktor;
            umsatzNeuMitarbeiterJahr += zusatzMonat * zeitraum;
            materialZusatzNeuMitarbeiterMonat += roundEuro(zusatzMonat * istMaterialQuote);
        }
        if (pa.entlassen && num(pa.istWert) > 0) {
            const faktor = num(pa.bisherigerUmsatzFaktor ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER);
            const auffang = num(pa.umsatzAuffangProzent ?? PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT);
            const bisherigerUmsatzMonat = num(pa.istWert) * faktor;
            const verlorenMonat = bisherigerUmsatzMonat - bisherigerUmsatzMonat * (auffang / 100);
            umsatzEntlassungVerlustJahr += verlorenMonat * zeitraum;
        }
    }

    return {
        umsatzNeuMitarbeiterJahr: roundEuro(umsatzNeuMitarbeiterJahr),
        umsatzEntlassungVerlustJahr: roundEuro(umsatzEntlassungVerlustJahr),
        materialZusatzNeuMitarbeiterMonat: roundEuro(materialZusatzNeuMitarbeiterMonat),
    };
};

const splitPersonalPrognose = (
    prognoseGesamtMonat: number,
    produktivIst: number,
    unproduktivIst: number
): { produktiv: number; unproduktiv: number } => {
    const istGesamt = num(produktivIst) + num(unproduktivIst);
    if (istGesamt <= 0) {
        return { produktiv: roundEuro(prognoseGesamtMonat), unproduktiv: 0 };
    }
    return {
        produktiv: roundEuro((num(prognoseGesamtMonat) * num(produktivIst)) / istGesamt),
        unproduktiv: roundEuro((num(prognoseGesamtMonat) * num(unproduktivIst)) / istGesamt),
    };
};

const buildAbweichung = (ist: PEPnlBlock, prognose: PEPnlBlock): PEPnlAbweichung => ({
    umsatzNetto: prognose.umsatzNetto - ist.umsatzNetto,
    wareneinsatz: prognose.wareneinsatz - ist.wareneinsatz,
    rohertrag: prognose.rohertrag - ist.rohertrag,
    personalkostenProduktiv: prognose.personalkostenProduktiv - ist.personalkostenProduktiv,
    personalkostenUnproduktiv: prognose.personalkostenUnproduktiv - ist.personalkostenUnproduktiv,
    betriebskosten: prognose.betriebskosten - ist.betriebskosten,
    geraetekosten: prognose.geraetekosten - ist.geraetekosten,
    sonstigeEinnahmen: prognose.sonstigeEinnahmen - ist.sonstigeEinnahmen,
    investitionen: prognose.investitionen - ist.investitionen,
    gesamtkosten: prognose.gesamtkosten - ist.gesamtkosten,
    betriebsergebnis: prognose.betriebsergebnis - ist.betriebsergebnis,
    einkommensteuer: prognose.einkommensteuer - ist.einkommensteuer,
    inhaberertrag: prognose.inhaberertrag - ist.inhaberertrag,
    rentabilitaetProzent: prognose.rentabilitaetProzent - ist.rentabilitaetProzent,
});

const scalePnlBlock = (block: PEPnlBlock, factor: number): PEPnlBlock => ({
    umsatzNetto: roundEuro(block.umsatzNetto * factor),
    wareneinsatz: roundEuro(block.wareneinsatz * factor),
    rohertrag: roundEuro(block.rohertrag * factor),
    personalkostenProduktiv: roundEuro(block.personalkostenProduktiv * factor),
    personalkostenUnproduktiv: roundEuro(block.personalkostenUnproduktiv * factor),
    betriebskosten: roundEuro(block.betriebskosten * factor),
    geraetekosten: roundEuro(block.geraetekosten * factor),
    sonstigeEinnahmen: roundEuro(block.sonstigeEinnahmen * factor),
    investitionen: roundEuro(block.investitionen * factor),
    gesamtkosten: roundEuro(block.gesamtkosten * factor),
    betriebsergebnis: roundEuro(block.betriebsergebnis * factor),
    einkommensteuer: roundEuro(block.einkommensteuer * factor),
    inhaberertrag: roundEuro(block.inhaberertrag * factor),
    rentabilitaetProzent: block.rentabilitaetProzent,
    materialquoteProzent: block.materialquoteProzent,
});

const scaleAbweichung = (abweichung: PEPnlAbweichung, factor: number): PEPnlAbweichung => ({
    umsatzNetto: roundEuro(abweichung.umsatzNetto * factor),
    wareneinsatz: roundEuro(abweichung.wareneinsatz * factor),
    rohertrag: roundEuro(abweichung.rohertrag * factor),
    personalkostenProduktiv: roundEuro(abweichung.personalkostenProduktiv * factor),
    personalkostenUnproduktiv: roundEuro(abweichung.personalkostenUnproduktiv * factor),
    betriebskosten: roundEuro(abweichung.betriebskosten * factor),
    geraetekosten: roundEuro(abweichung.geraetekosten * factor),
    sonstigeEinnahmen: roundEuro(abweichung.sonstigeEinnahmen * factor),
    investitionen: roundEuro(abweichung.investitionen * factor),
    gesamtkosten: roundEuro(abweichung.gesamtkosten * factor),
    betriebsergebnis: roundEuro(abweichung.betriebsergebnis * factor),
    einkommensteuer: roundEuro(abweichung.einkommensteuer * factor),
    inhaberertrag: roundEuro(abweichung.inhaberertrag * factor),
    rentabilitaetProzent: abweichung.rentabilitaetProzent,
});

const applyAnsichtsModus = (
    result: PEProfitCalculations,
    ansichtsModus: PEProfitAnsichtsModus,
    zeitraum: number
): PEProfitCalculations => {
    if (ansichtsModus !== 'jahr') {
        return { ...result, ansichtsModus };
    }

    return {
        ...result,
        ansichtsModus,
        ist: scalePnlBlock(result.ist, zeitraum),
        prognose: scalePnlBlock(result.prognose, zeitraum),
        abweichung: scaleAbweichung(result.abweichung, zeitraum),
    };
};

export const calculatePeProfitMetrics = (
    calc: CalcSnapshotWithPlanungsBasis,
    peProfitPrognose: PEProfitPrognose,
    dataVersion = 0
): PEProfitCalculations => {
    if (!calc?.ready) {
        return {
            ready: false,
            zeitraum: 12,
            szenarioName: peProfitPrognose.szenarioName,
            ansichtsModus: peProfitPrognose.ansichtsModus || 'monat',
            ist: emptyPnlBlock(),
            prognose: emptyPnlBlock(),
            abweichung: buildAbweichung(emptyPnlBlock(), emptyPnlBlock()),
            meta: {
                preisAnpassungProzent: peProfitPrognose.preisAnpassungProzent,
                kundenVerlustProzent: calcAutoKundenVerlustProzent(peProfitPrognose.preisAnpassungProzent),
                materialKostenAnpassungProzent: peProfitPrognose.materialKostenAnpassungProzent,
                zusaetzlicherUmsatzJahr: 0,
                personalkostenDeltaMonat: 0,
                investitionenMonat: 0,
                umsatzJahr: { ist: 0, prognose: 0 },
                materialIstMonat: 0,
                materialPrognoseMonat: 0,
                betriebskostenIstMonat: 0,
                betriebskostenPrognoseMonat: 0,
                personalkostenIstMonat: 0,
                personalkostenPrognoseMonat: 0,
                umsatzNeuMitarbeiterJahr: 0,
                umsatzEntlassungVerlustJahr: 0,
                materialZusatzNeuMitarbeiterMonat: 0,
            },
            storeVersion: dataVersion,
        };
    }

    const zeitraum = num(calc.zeitraum) || 12;
    const bwaData = calc.bwaData || {};
    const materialJahr = num(bwaData.material);
    const hasPlanungsBasisOverride = calc.peProfitBasisUmsatzMonat !== undefined;

    let istUmsatzJahr = num(bwaData.umsatz);
    let istUmsatzMonat = safeDiv(istUmsatzJahr, zeitraum);

    if (hasPlanungsBasisOverride) {
        istUmsatzMonat = num(calc.peProfitBasisUmsatzMonat);
        istUmsatzJahr = num(calc.peProfitBasisUmsatzJahr ?? istUmsatzMonat * zeitraum);
    }

    const istWareneinsatzMonat = safeDiv(materialJahr, zeitraum);

    // P&L-Basis IST (Monatswerte — identisch zur Tabelle vor Jahres-Skalierung)
    const personalkostenProduktivIst = num(calc.produktiveKostenGesamtMonat);
    const personalkostenUnproduktivIst =
        num(calc.unproduktiveLohnkostenMitarbeiter) + num(calc.unprodUnternehmerlohn);
    const istPersonalkostenMonat = roundEuro(personalkostenProduktivIst + personalkostenUnproduktivIst);
    const istBetriebskostenMonat = roundEuro(num(calc.betriebskostenMonat));
    const geraetekostenIst = num(calc.geraetekostenMonat);
    const sonstigeEinnahmenIst = num(calc.positiveWerteMonat);
    const istBetriebsergebnisBwa = roundEuro(istUmsatzMonat - num(calc.sollumsatz));
    const einkommensteuerIst = num(calc.erwarteteEinkommenssteuer);
    const unternehmerBedarfMonat = num(calc.gesamtlohn);

    const ist = buildPnlBlock({
        umsatzNetto: istUmsatzMonat,
        wareneinsatz: istWareneinsatzMonat,
        personalkostenProduktiv: personalkostenProduktivIst,
        personalkostenUnproduktiv: personalkostenUnproduktivIst,
        betriebskosten: istBetriebskostenMonat,
        geraetekosten: geraetekostenIst,
        sonstigeEinnahmen: sonstigeEinnahmenIst,
        investitionen: 0,
        einkommensteuer: einkommensteuerIst,
        unternehmerBedarfMonat,
        istUmsatzJahr,
        materialJahr,
        betriebsergebnisOverride: istBetriebsergebnisBwa,
    });

    if (isNeutralPeProfitPrognose(peProfitPrognose)) {
        const prognosePnl = clonePnlBlock(ist);
        const baseResult: PEProfitCalculations = {
            ready: true,
            zeitraum,
            szenarioName: peProfitPrognose.szenarioName,
            ansichtsModus: peProfitPrognose.ansichtsModus || 'monat',
            ist,
            prognose: prognosePnl,
            abweichung: buildAbweichung(ist, ist),
            meta: {
                preisAnpassungProzent: 0,
                kundenVerlustProzent: 0,
                materialKostenAnpassungProzent: 0,
                zusaetzlicherUmsatzJahr: 0,
                personalkostenDeltaMonat: 0,
                investitionenMonat: 0,
                umsatzJahr: {
                    ist: istUmsatzJahr,
                    prognose: istUmsatzJahr,
                },
                materialIstMonat: istWareneinsatzMonat,
                materialPrognoseMonat: istWareneinsatzMonat,
                betriebskostenIstMonat: istBetriebskostenMonat,
                betriebskostenPrognoseMonat: istBetriebskostenMonat,
                personalkostenIstMonat: istPersonalkostenMonat,
                personalkostenPrognoseMonat: istPersonalkostenMonat,
                umsatzNeuMitarbeiterJahr: 0,
                umsatzEntlassungVerlustJahr: 0,
                materialZusatzNeuMitarbeiterMonat: 0,
            },
            storeVersion: dataVersion,
        };

        return applyAnsichtsModus(baseResult, peProfitPrognose.ansichtsModus || 'monat', zeitraum);
    }

    const preisAnpassungBetragJahr = istUmsatzJahr * (num(peProfitPrognose.preisAnpassungProzent) / 100);
    const umsatzNachPreisJahr = istUmsatzJahr + preisAnpassungBetragJahr;
    const kundenVerlustProzent =
        peProfitPrognose.kundenVerlustProzent !== null &&
        peProfitPrognose.kundenVerlustProzent !== undefined
            ? num(peProfitPrognose.kundenVerlustProzent)
            : calcAutoKundenVerlustProzent(peProfitPrognose.preisAnpassungProzent);
    const umsatzNachSchwundJahr = umsatzNachPreisJahr * (1 - kundenVerlustProzent / 100);
    const dienstleistungsUmsatzJahr = (peProfitPrognose.neueDienstleistungen ?? [])
        .filter((dl) => dl.aktiv)
        .reduce((sum, dl) => sum + num(dl.jahresumsatzNetto), 0);

    const gemeinkostenDetailAnpassungen = [...(peProfitPrognose.gemeinkostenDetailAnpassungen ?? [])];
    const personalAnpassungen = [...(peProfitPrognose.personalAnpassungen ?? [])];
    const personalAnpassungenAktiv = personalAnpassungen.filter((item) => item.aktiv);
    const istMaterialQuote = safeDiv(materialJahr, istUmsatzJahr);
    const personalDynamik = calcPersonalUmsatzDynamik(
        personalAnpassungenAktiv,
        zeitraum,
        istMaterialQuote
    );

    const unternehmerUmsatzEffekt = calcUnternehmerUmsatzNettoJahr(
        peProfitPrognose.unternehmerUmsatzDeltaMonat ?? 0,
        peProfitPrognose.unternehmerUmsatzAuffangProzent ?? PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
        zeitraum
    );

    const prognoseUmsatzJahr =
        umsatzNachSchwundJahr +
        dienstleistungsUmsatzJahr +
        personalDynamik.umsatzNeuMitarbeiterJahr -
        personalDynamik.umsatzEntlassungVerlustJahr +
        unternehmerUmsatzEffekt.nettoAufGesamtJahr;
    const prognoseUmsatzMonat = safeDiv(prognoseUmsatzJahr, zeitraum);

    const materialZielMonat = peProfitPrognose.materialZielMonat ?? null;
    // Ohne expliziten Material-Zielwert: IST-Material beibehalten (Preiserhöhung ≠ mehr Verbrauch)
    let prognoseWareneinsatzMonat =
        materialZielMonat !== null && materialZielMonat !== undefined
            ? roundEuro(materialZielMonat)
            : peProfitPrognose.materialKostenAnpassungProzent > 0
              ? roundEuro(
                    istWareneinsatzMonat *
                        (1 - num(peProfitPrognose.materialKostenAnpassungProzent) / 100)
                )
              : istWareneinsatzMonat;
    prognoseWareneinsatzMonat = roundEuro(
        prognoseWareneinsatzMonat + personalDynamik.materialZusatzNeuMitarbeiterMonat
    );

    const personalPrognoseMonat = calcPersonalPrognoseMonat(istPersonalkostenMonat, personalAnpassungenAktiv);
    const personalSplit = splitPersonalPrognose(
        personalPrognoseMonat,
        personalkostenProduktivIst,
        personalkostenUnproduktivIst
    );
    const personalkostenDeltaMonat = roundEuro(personalPrognoseMonat - istPersonalkostenMonat);

    const gkPrognoseMonat = calcGkPrognoseMonat(istBetriebskostenMonat, gemeinkostenDetailAnpassungen);

    const prognoseInvestitionenMonat = roundEuro(
        (peProfitPrognose.neueInvestitionen ?? [])
            .filter((inv) => inv.aktiv)
            .reduce((sum, inv) => sum + Math.round(Number(inv.monatskosten)), 0)
    );

    const hasMaterialCostAdjust =
        (materialZielMonat !== null && materialZielMonat !== undefined) ||
        peProfitPrognose.materialKostenAnpassungProzent > 0 ||
        personalDynamik.materialZusatzNeuMitarbeiterMonat > 0;
    const hasPersonalCostAdjust = personalAnpassungenAktiv.length > 0;
    const hasGkCostAdjust = gemeinkostenDetailAnpassungen.length > 0;
    const hasInvestitionAdjust = (peProfitPrognose.neueInvestitionen ?? []).some((inv) => inv.aktiv);

    const prognoseWareneinsatz = hasMaterialCostAdjust ? prognoseWareneinsatzMonat : ist.wareneinsatz;
    const prognosePersonalkostenProduktiv = hasPersonalCostAdjust
        ? personalSplit.produktiv
        : ist.personalkostenProduktiv;
    const prognosePersonalkostenUnproduktiv = hasPersonalCostAdjust
        ? personalSplit.unproduktiv
        : ist.personalkostenUnproduktiv;
    const prognoseBetriebskosten = hasGkCostAdjust ? gkPrognoseMonat : ist.betriebskosten;
    const prognoseGeraetekosten = ist.geraetekosten;
    const prognoseSonstigeEinnahmen = ist.sonstigeEinnahmen;
    const prognoseInvestitionen = hasInvestitionAdjust ? prognoseInvestitionenMonat : ist.investitionen;

    const prognoseBetriebsergebnis = calcDeltaBetriebsergebnis({
        ist,
        prognoseUmsatz: prognoseUmsatzMonat,
        prognoseWareneinsatz,
        prognosePersonalkostenProduktiv,
        prognosePersonalkostenUnproduktiv,
        prognoseBetriebskosten,
        prognoseGeraetekosten,
        prognoseSonstigeEinnahmen,
        prognoseInvestitionen,
    });

    const unternehmerLohnIstMonat = unternehmerBedarfMonat;
    // Unternehmerlohn/Entnahme-Override entfernt: Prognose nutzt immer den IST-Bedarf
    const unternehmerLohnPrognoseMonat = unternehmerLohnIstMonat;
    const einkommensteuerPrognose = einkommensteuerIst;

    const prognosePnl = buildLinearPrognosePnl({
        umsatzNetto: prognoseUmsatzMonat,
        wareneinsatz: prognoseWareneinsatz,
        personalkostenProduktiv: prognosePersonalkostenProduktiv,
        personalkostenUnproduktiv: prognosePersonalkostenUnproduktiv,
        betriebskosten: prognoseBetriebskosten,
        geraetekosten: prognoseGeraetekosten,
        sonstigeEinnahmen: prognoseSonstigeEinnahmen,
        investitionen: prognoseInvestitionen,
        einkommensteuer: einkommensteuerPrognose,
        unternehmerBedarfMonat: unternehmerLohnPrognoseMonat,
        betriebsergebnis: prognoseBetriebsergebnis,
    });

    const baseResult: PEProfitCalculations = {
        ready: true,
        zeitraum,
        szenarioName: peProfitPrognose.szenarioName,
        ansichtsModus: peProfitPrognose.ansichtsModus || 'monat',
        ist,
        prognose: prognosePnl,
        abweichung: buildAbweichung(ist, prognosePnl),
        meta: {
            preisAnpassungProzent: peProfitPrognose.preisAnpassungProzent,
            kundenVerlustProzent,
            materialKostenAnpassungProzent: peProfitPrognose.materialKostenAnpassungProzent,
            zusaetzlicherUmsatzJahr: prognoseUmsatzJahr - istUmsatzJahr,
            personalkostenDeltaMonat,
            investitionenMonat: prognoseInvestitionenMonat,
            umsatzJahr: {
                ist: istUmsatzJahr,
                prognose: prognoseUmsatzJahr,
            },
            materialIstMonat: istWareneinsatzMonat,
            materialPrognoseMonat: prognoseWareneinsatz,
            betriebskostenIstMonat: istBetriebskostenMonat,
            betriebskostenPrognoseMonat: prognoseBetriebskosten,
            personalkostenIstMonat: istPersonalkostenMonat,
            personalkostenPrognoseMonat: roundEuro(
                prognosePersonalkostenProduktiv + prognosePersonalkostenUnproduktiv
            ),
            umsatzNeuMitarbeiterJahr: personalDynamik.umsatzNeuMitarbeiterJahr,
            umsatzEntlassungVerlustJahr: personalDynamik.umsatzEntlassungVerlustJahr,
            materialZusatzNeuMitarbeiterMonat: personalDynamik.materialZusatzNeuMitarbeiterMonat,
            unternehmerLohnIstMonat,
            unternehmerLohnPrognoseMonat,
            unternehmerUmsatzDeltaJahr: unternehmerUmsatzEffekt.deltaJahr,
            unternehmerUmsatzNettoAufGesamtJahr: unternehmerUmsatzEffekt.nettoAufGesamtJahr,
        },
        storeVersion: dataVersion,
    };

    return applyAnsichtsModus(baseResult, peProfitPrognose.ansichtsModus || 'monat', zeitraum);
};

type SollIstRowDef = {
    key: string;
    label: string;
    getValue: (pnl: PEPnlBlock) => number;
    higherIsBetter: boolean;
    isPercent?: boolean;
};

const SOLL_IST_ROWS: SollIstRowDef[] = [
    {
        key: 'umsatzNetto',
        label: 'Umsatzerlöse (netto)',
        getValue: (pnl) => pnl.umsatzNetto,
        higherIsBetter: true,
    },
    {
        key: 'betriebsergebnis',
        label: 'Betriebsergebnis',
        getValue: (pnl) => pnl.betriebsergebnis,
        higherIsBetter: true,
    },
    {
        key: 'inhaberertrag',
        label: 'Inhaberertrag',
        getValue: (pnl) => pnl.inhaberertrag,
        higherIsBetter: true,
    },
    {
        key: 'rentabilitaetProzent',
        label: 'Rentabilität',
        getValue: (pnl) => pnl.rentabilitaetProzent,
        higherIsBetter: true,
        isPercent: true,
    },
];

export const calcSollIstVergleich = (
    sollPnlInput: PEPnlBlock,
    istPnlInput: PEPnlBlock
): SollIstVergleichRow[] => {
    const sollPnl: PEPnlBlock = { ...sollPnlInput };
    const istPnl: PEPnlBlock = { ...istPnlInput };

    return SOLL_IST_ROWS.map((row) => {
        const soll = row.getValue(sollPnl);
        const ist = row.getValue(istPnl);
        const delta = ist - soll;
        const zielErreicht = row.higherIsBetter ? ist >= soll : ist <= soll;

        return {
            key: row.key,
            label: row.label,
            soll,
            ist,
            delta,
            zielErreicht,
            isPercent: row.isPercent,
            isCost: !row.higherIsBetter,
        };
    });
};

/** Live-Ist-PnL für Soll-Ist-Vergleich — immer basisDatenTyp „aktuell“, neutral (ohne Cockpit). */
export const calculateLiveIstPnl = (
    calc: CalcSnapshot,
    clientData: unknown,
    settings: unknown,
    dataVersion = 0
): PEPnlBlock => {
    const calcForAktuell = resolveCalcForPlanungsBasis(calc, 'aktuell', clientData, settings);
    const neutralPrognose = createDefaultPEProfitPrognose();
    return clonePnlBlock(calculatePeProfitMetrics(calcForAktuell, neutralPrognose, dataVersion).ist);
};

export const usePeProfitLiveIstPnl = (): PEPnlBlock => {
    const calc = useCalculationsSelector();
    const { clientData, settings, storeVersion } = useAppStore(
        useShallow((state) => ({
            clientData: state.clientData,
            settings: state.settings,
            storeVersion: state.peProfitDataVersion ?? 0,
        }))
    );

    return useMemo(
        () => calculateLiveIstPnl(calc, clientData, settings, storeVersion),
        [calc, clientData, settings, storeVersion]
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// REACT HOOK
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const usePeProfitCalculations = (): PEProfitCalculations => {
    const calc = useCalculationsSelector();
    const {
        prognose,
        storeVersion,
        gkAnpassungen,
        personalAnpassungen,
        neueDienstleistungen,
        neueInvestitionen,
        clientData,
        settings,
        activeSzenarioId,
        planungsArchiv,
        peProfitBasisDatenTyp,
    } = useAppStore(
        useShallow((state) => {
            const ws = selectActivePeProfitWorkspace(state);
            return {
                prognose: ws.prognose,
                storeVersion: state.peProfitDataVersion ?? 0,
                gkAnpassungen: ws.prognose?.gemeinkostenDetailAnpassungen,
                personalAnpassungen: ws.prognose?.personalAnpassungen,
                neueDienstleistungen: ws.prognose?.neueDienstleistungen,
                neueInvestitionen: ws.prognose?.neueInvestitionen,
                clientData: state.clientData,
                settings: state.settings,
                activeSzenarioId: ws.activeSzenarioId,
                planungsArchiv: ws.planungsArchiv,
                peProfitBasisDatenTyp: ws.basisDatenTyp,
            };
        })
    );

    const basisDatenTyp: PlanungsBasisDatenTyp = useMemo(() => {
        if (activeSzenarioId) {
            const entry = planungsArchiv.find((item) => item.id === activeSzenarioId);
            return entry?.basisDatenTyp ?? 'start';
        }
        return peProfitBasisDatenTyp;
    }, [activeSzenarioId, planungsArchiv, peProfitBasisDatenTyp]);

    const calcForBasis = useMemo(
        () => resolveCalcForPlanungsBasis(calc, basisDatenTyp, clientData, settings),
        [calc, basisDatenTyp, clientData, settings]
    );

    const prognoseForCalc: PEProfitPrognose = {
        ...prognose,
        gemeinkostenDetailAnpassungen: gkAnpassungen ?? prognose.gemeinkostenDetailAnpassungen ?? [],
        personalAnpassungen: personalAnpassungen ?? prognose.personalAnpassungen ?? [],
        neueDienstleistungen: neueDienstleistungen ?? prognose.neueDienstleistungen ?? [],
        neueInvestitionen: neueInvestitionen ?? prognose.neueInvestitionen ?? [],
    };

    return calculatePeProfitMetrics(calcForBasis, prognoseForCalc, storeVersion);
};
